/**
 * ConfettiFX — estouro de confetes ao redor de um avatar (celebração de conquista).
 *
 * Um burst = UM InstancedMesh de N quadrados coloridos → 1 draw call, independente do
 * nº de confetes (Sprites custariam 1 draw call CADA — inviável no telão de 8h). Sem
 * sombras, sem toneMapped custom (segue o padrão do TransformFX, p/ não brigar com o
 * bloom). Depois de play(), o burst vive em ESPAÇO-MUNDO próprio — desacoplado do
 * avatar — então completa os ~3s mesmo se o jogador andar/despawnar.
 *
 * Pool no EntityManager (this._confFree/_confActive): update(dt) retorna `true` quando
 * termina → o dono recicla. Zero alocação por frame (temporários de módulo).
 */
import * as THREE from 'three';

const N = 48;        // confetes por burst
const LIFE = 3.0;    // s de vida (casa com o pulo de 3s)
const FADE = 0.9;    // s finais em fade-out
const GRAV = 9.0;    // gravidade
const DRAG = 0.8;    // arrasto horizontal (por s)

// temporários de módulo — reusados a cada frame (nada de alocar num run de horas)
const _m = new THREE.Matrix4();
const _p = new THREE.Vector3();
const _q = new THREE.Quaternion();
const _e = new THREE.Euler();
const _one = new THREE.Vector3(1, 1, 1);
const _col = new THREE.Color();
const COLORS = [0x2fce5e, 0xffd23f, 0x4ea3ff, 0xff5e5e, 0xc879e6, 0x39c6c6, 0xf5f5f5, 0xff8c3b];

export class ConfettiFX {
  constructor() {
    const geo = new THREE.PlaneGeometry(0.16, 0.26);
    // material próprio POR instância do pool (fade via opacity não vaza p/ outro burst)
    this._mat = new THREE.MeshBasicMaterial({ side: THREE.DoubleSide, transparent: true, depthWrite: false, opacity: 0 });
    this.mesh = new THREE.InstancedMesh(geo, this._mat, N);
    this.mesh.frustumCulled = false;                       // bounding é da geo (na origem) → não deixar cullar o burst espalhado
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage); // reescrito todo frame
    this.mesh.visible = false;
    this.mesh.setColorAt(0, _col.setHex(0xffffff));        // aloca o instanceColor
    this.group = this.mesh;                                // o pool adiciona this.group ao grupo de FX

    // estado por partícula em Float32Array (sem GC)
    this.px = new Float32Array(N); this.py = new Float32Array(N); this.pz = new Float32Array(N);
    this.vx = new Float32Array(N); this.vy = new Float32Array(N); this.vz = new Float32Array(N);
    this.rx = new Float32Array(N); this.ry = new Float32Array(N); this.rz = new Float32Array(N);
    this.ax = new Float32Array(N); this.ay = new Float32Array(N); this.az = new Float32Array(N);
    this.t = 0;
  }

  /** Dispara o burst na posição (x,y,z) — em espaço-mundo, independente do avatar.
   *  Opções p/ cenas em MINIATURA (retrato do chip de conquista):
   *    speed  — escala de velocidades/gravidade (mais lento = fica no quadro);
   *    size   — escala do papelzinho (maior = legível num retrato de 72px);
   *    floorY — plano do "chão" onde os confetes assentam (pés do avatar na cena).
   *  Na cidade, os defaults reproduzem o comportamento original. */
  play(x, y, z, { speed = 1, size = 1, floorY = 0.02 } = {}) {
    this.t = 0;
    this._grav = GRAV * speed;
    this._size = size;
    this._floorY = floorY;
    for (let i = 0; i < N; i++) {
      this.px[i] = x; this.py[i] = y; this.pz[i] = z;
      const ang = Math.random() * Math.PI * 2;
      const sp = (1.4 + Math.random() * 3.4) * speed;    // impulso radial (horizontal)
      this.vx[i] = Math.cos(ang) * sp;
      this.vy[i] = (3.6 + Math.random() * 4.6) * speed;  // pra cima
      this.vz[i] = Math.sin(ang) * sp;
      this.rx[i] = Math.random() * Math.PI * 2;
      this.ry[i] = Math.random() * Math.PI * 2;
      this.rz[i] = Math.random() * Math.PI * 2;
      this.ax[i] = (Math.random() - 0.5) * 14;           // tumble (vel. angular)
      this.ay[i] = (Math.random() - 0.5) * 14;
      this.az[i] = (Math.random() - 0.5) * 14;
      this.mesh.setColorAt(i, _col.setHex(COLORS[(Math.random() * COLORS.length) | 0]));
    }
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;
    this._mat.opacity = 1;
    this.mesh.visible = true;
    this._write();
  }

  /** Integra 1 frame. Retorna `true` quando o burst terminou (dono deve reciclar). */
  update(dt) {
    if (!this.mesh.visible) return true;
    this.t += dt;
    const drag = 1 - Math.min(1, dt * DRAG);
    const grav = this._grav ?? GRAV;
    const floor = this._floorY ?? 0.02;
    for (let i = 0; i < N; i++) {
      this.vy[i] -= grav * dt;
      this.px[i] += this.vx[i] * dt;
      this.py[i] += this.vy[i] * dt;
      this.pz[i] += this.vz[i] * dt;
      if (this.py[i] < floor) { this.py[i] = floor; this.vy[i] = 0; this.vx[i] = 0; this.vz[i] = 0; } // assenta no chão
      this.vx[i] *= drag; this.vz[i] *= drag;
      this.rx[i] += this.ax[i] * dt;
      this.ry[i] += this.ay[i] * dt;
      this.rz[i] += this.az[i] * dt;
    }
    this._write();
    this._mat.opacity = this.t < LIFE - FADE ? 1 : Math.max(0, (LIFE - this.t) / FADE);
    if (this.t >= LIFE) { this.hide(); return true; }
    return false;
  }

  _write() {
    const s = this._size ?? 1;
    _one.set(s, s, s);
    for (let i = 0; i < N; i++) {
      _p.set(this.px[i], this.py[i], this.pz[i]);
      _e.set(this.rx[i], this.ry[i], this.rz[i]);
      _q.setFromEuler(_e);
      _m.compose(_p, _q, _one);
      this.mesh.setMatrixAt(i, _m);
    }
    this.mesh.instanceMatrix.needsUpdate = true;
  }

  hide() {
    this.mesh.visible = false;
    this._mat.opacity = 0;
  }
}
